import { Box } from "@mui/material";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "../../service-quote-form/components/DatePicker";
import QuoteInputCard from "../../service-quote-form/components/QuoteInputCard";
import TimePicker from "../../service-quote-form/components/TimePicker";
import ChurchLocationPicker, { ChurchDetails } from "./ChurchLocationPicker";
import ChurchStreamDetailsModal from "./ChurchStreamDetailsModal";
import ChurchThankYouModal from "./ChurchThankYouModal";
import { ClientInfoModal } from "./ClientDetails";

const ChurchBookingForm = () => {
  const [when, setWhen] = useState("");
  const [where, setWhere] = useState<ChurchDetails>({
    churchName: "",
    address: "",
    suburb: "",
  });
  const [whatTime, setWhatTime] = useState("");

  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [whereAnchor, setWhereAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);

  const whereBoxRef = useRef<HTMLDivElement>(null);
  const timeBoxRef = useRef<HTMLDivElement>(null);

  const [showClientModal, setShowClientModal] = useState(false);
  const [userType, setUserType] = useState<"pastor" | "booking-for-pastor">(
    "pastor",
  );
  const [clientInfo, setClientInfo] = useState({
    fullName: "",
    whatsappNumber: "",
  });
  const [pastorInfo, setPastorInfo] = useState({
    fullName: "",
    whatsappNumber: "",
  });

  const [showChurchDetailsModal, setShowChurchDetailsModal] = useState(false);
  const [leadPastor, setLeadPastor] = useState("");
  const [congregationSize, setCongregationSize] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [churchPhotoPreview, setChurchPhotoPreview] = useState<string | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [applicationReference, setApplicationReference] = useState("");

  const handleChurchPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setChurchPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };


  const formatDate = (date: string) => {
    if (!date) return "Add dates";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <Box>
      <QuoteInputCard
        when={when}
        where={where.churchName ? `${where.churchName}, ${where.suburb}` : ""}
        whatTime={whatTime}
        whereBoxRef={whereBoxRef}
        timeBoxRef={timeBoxRef}
        onWhenClick={(el) => setWhenAnchor(el)}
        onWhereClick={(el) => setWhereAnchor(el)}
        onTimeClick={(el) => setTimeAnchor(el)}
        onGetQuote={() => {
          if (
            !when ||
            !where.churchName ||
            !where.address ||
            !where.suburb ||
            !whatTime
          ) {
            toast.error("Please fill in all fields");
            return;
          }
          setShowClientModal(true);
        }}
        formatDate={formatDate}
        formatTime={formatTime}
        buttonText="Apply"
      />

      <DatePicker
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={when}
        onChange={(date) => {
          setWhen(date);
          setWhenAnchor(null);
          if (whereBoxRef.current) setWhereAnchor(whereBoxRef.current);
        }}
      />

      <ChurchLocationPicker
        open={Boolean(whereAnchor)}
        anchorEl={whereAnchor}
        onClose={() => {
          setWhereAnchor(null);
          if (
            where.churchName &&
            where.address &&
            where.suburb &&
            timeBoxRef.current
          ) {
            setTimeAnchor(timeBoxRef.current);
          }
        }}
        value={where}
        onChange={(details) => setWhere(details)}
      />

      <TimePicker
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={whatTime}
        onChange={(time) => {
          setWhatTime(time);
          setTimeAnchor(null);
        }}
      />

      <ClientInfoModal
        open={showClientModal}
        onClose={() => setShowClientModal(false)}
        userType={userType}
        onUserTypeChange={setUserType}
        clientInfo={clientInfo}
        onClientInfoChange={setClientInfo}
        pastorInfo={pastorInfo}
        onPastorInfoChange={setPastorInfo}
        onSubmit={() => {
          setShowClientModal(false);
          setShowChurchDetailsModal(true);
        }}
      />

      <ChurchStreamDetailsModal
        open={showChurchDetailsModal}
        onClose={() => setShowChurchDetailsModal(false)}
        leadPastor={leadPastor}
        congregationSize={congregationSize}
        websiteLink={websiteLink}
        facebookLink={facebookLink}
        churchPhotoPreview={churchPhotoPreview}
        onLeadPastorChange={setLeadPastor}
        onCongregationSizeChange={setCongregationSize}
        onWebsiteLinkChange={setWebsiteLink}
        onFacebookLinkChange={setFacebookLink}
        onChurchPhotoUpload={handleChurchPhotoUpload}
        isSubmitting={isSubmitting}
        onSubmit={async () => {
          setIsSubmitting(true);

          

          try {
            await new Promise((resolve) => setTimeout(resolve, 2000));

            const refNumber = "";
            setApplicationReference(refNumber);

            setShowChurchDetailsModal(false);
            setShowThankYouModal(true);
          } catch (error) {
            toast.error("Failed to submit application. Please try again.");
            console.error("Submission error:", error);
          } finally {
            setIsSubmitting(false);
          }
        }}
      />

      <ChurchThankYouModal
        open={showThankYouModal}
        onClose={() => {
          setShowThankYouModal(false);
        }}
        referenceNumber={applicationReference}
        //churchName={where.churchName}
        onCompleteDetails={() => {
          setShowThankYouModal(false);
          setShowChurchDetailsModal(true);
        }}
      />
    </Box>
  );
};

export default ChurchBookingForm;
